import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HIGHLIGHTS, type Highlight } from '../data/highlights.js';
import { cn } from '../lib/cn.js';
import { Button } from './ui/button.js';

/**
 * Carrossel de destaques do dashboard. Avança sozinho a cada 8 segundos e para quando
 * o cursor está em cima — ninguém lê um cartão que some no meio da frase.
 */
export function HighlightsCarousel({ items = HIGHLIGHTS }: { items?: readonly Highlight[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const current = items[index] ?? items[0];

  useEffect(() => {
    if (paused || items.length < 2) return;
    const timer = setInterval(() => setIndex((value) => (value + 1) % items.length), 8_000);
    return () => clearInterval(timer);
  }, [paused, items.length]);

  if (!current) return null;
  const go = (delta: number) => setIndex((value) => (value + delta + items.length) % items.length);

  return (
    <section
      aria-label="Destaques"
      aria-roledescription="carrossel"
      className="relative overflow-hidden rounded-lg border border-border bg-gradient-to-br from-primary-soft via-card to-card p-5 shadow-card"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 max-w-xl" aria-live="polite">
          <div className="mb-1 text-[11px] font-semibold tracking-wide text-primary uppercase">
            {current.eyebrow}
          </div>
          <h3 className="m-0 text-lg font-semibold">{current.title}</h3>
          <p className="mt-1 mb-3 text-sm text-muted-foreground">{current.body}</p>
          <Button size="sm" asChild>
            <Link to={current.to} className="no-underline">
              {current.cta} <ArrowRight />
            </Link>
          </Button>
        </div>
        {items.length > 1 && (
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Destaque anterior"
              onClick={() => go(-1)}
            >
              <ChevronLeft />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Próximo destaque"
              onClick={() => go(1)}
            >
              <ChevronRight />
            </Button>
          </div>
        )}
      </div>
      {items.length > 1 && (
        <div className="mt-3 flex gap-1.5" role="tablist" aria-label="Destaques">
          {items.map((item, i) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={item.title}
              onClick={() => setIndex(i)}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === index ? 'w-6 bg-primary' : 'w-1.5 bg-border hover:bg-subtle',
              )}
            />
          ))}
        </div>
      )}
    </section>
  );
}
